import { useEffect, useState, useCallback, useMemo } from "react";
import styled from "@emotion/styled";
import { Row, Col, Spinner, Form } from "react-bootstrap";
import { COLORS } from "constant";
import { isSensor, isActuator } from "utils/functions";
import { ColorPicker } from "../create";
import { LDSUDevice } from "../create/LDSUWidget/LDSUWidgetSensorList";
import { getLDSUListByGatewayIds } from "service/gatewayService";
import { LDSU } from "types";
import { APP_CONFIG } from "config/appConfig";
import { GATEWAY_LABEL, LDSU_UUID_LABEL } from "constant/displayLabels";

const ldsuDisplayName = APP_CONFIG.APP_IS_ONPREM ? "LDSU/Auxiliary Hub" : "LDSU";

const Wrapper = styled.div`
    background-color: #2f3a51;
    padding: 20px;
    border-radius: 10px;
    margin-bottom: 20px;

    .table-line {
        margin-top: -0.75rem;
        margin-bottom: -0.75rem;
    }

    .update-row {
        height: 3.5rem;
    }

    .mute {
        color: #828fab;
    }
`;

type UpdateLDSUCardProps = {
    /** Panel data for update mode */
    panel: any;
    /** Device list (used to map attributes to device info) */
    devices: any[];
    /** Called when data changes */
    onChange?: (field: string, value: any) => void;
};

/**
 * Update LDSU Card component - Display Widget Name, LDSU Name, Gateway Name, LDSU UUID
 * and Sensor/Actuator lists for editing
 */
const UpdateLDSUCard = ({ panel, devices, onChange }: UpdateLDSUCardProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingLDSU, setIsLoadingLDSU] = useState(false);
    const [filterDevices, setFilterDevices] = useState<{
        sensors: LDSUDevice[];
        actuators: LDSUDevice[];
    }>({ sensors: [], actuators: [] });
    const [chartName, setChartName] = useState(panel?.name || "");
    const [ldsuInfo, setLdsuInfo] = useState<LDSU | null>(null);

    // Update chartName when panel changes
    useEffect(() => {
        if (panel?.name) {
            setChartName(panel.name);
        }
    }, [panel?.name]);

    // Handle widget name change
    const handleNameChange = (value: string) => {
        setChartName(value);
        onChange?.("name", value);
    };

    // Extract LDSU info from panel attributes
    const ldsuUuid = useMemo(() => {
        return panel?.attributes?.[0]?.device_id || "";
    }, [panel]);

    const gatewayId = useMemo(() => {
        return panel?.attributes?.[0]?.gateway_id || "";
    }, [panel]);

    // Fetch LDSU info using the service
    useEffect(() => {
        const fetchLDSUInfo = async () => {
            if (!gatewayId || !ldsuUuid) {
                setLdsuInfo(null);
                return;
            }

            setIsLoadingLDSU(true);
            try {
                const ldsuList = await getLDSUListByGatewayIds([gatewayId]);
                const foundLDSU = ldsuList.find(
                    (ldsu) => ldsu.ldsu_uuid === ldsuUuid
                );
                setLdsuInfo(foundLDSU || null);
            } catch (error) {
                console.error("Error fetching LDSU info:", error);
                setLdsuInfo(null);
            } finally {
                setIsLoadingLDSU(false);
            }
        };

        fetchLDSUInfo();
    }, [gatewayId, ldsuUuid]);

    // Get gateway name from LDSU info or fallback to devices
    const gatewayName = useMemo(() => {
        if (ldsuInfo?.gatewayName) return ldsuInfo.gatewayName;
        const device = devices?.find((d: any) => d.gatewayId === gatewayId);
        return device?.gatewayName || "-";
    }, [ldsuInfo, devices, gatewayId]);

    // Get LDSU name from fetched info or fallback
    const ldsuName = useMemo(() => {
        if (ldsuInfo?.name) return ldsuInfo.name;
        // Look in devices for matching LDSU
        const device = devices?.find((d: any) => d.ldsuUID === ldsuUuid);
        return device?.ldsuName || device?.ldsu_name || "-";
    }, [ldsuInfo, devices, ldsuUuid]);

    // Filter devices by LDSU UUID to get sensors and actuators
    useEffect(() => {
        if (!ldsuUuid || !devices?.length) {
            setFilterDevices({ sensors: [], actuators: [] });
            return;
        }

        setIsLoading(true);

        try {
            // Filter sensors from devices matching this LDSU UUID
            const filteredSensors = devices
                .filter(
                    (device: any) =>
                        device.ldsuUID === ldsuUuid && isSensor(device.cls)
                )
                .map((sensor: any, index: number) => {
                    // Find matching attribute color from panel
                    const matchingAttr = panel?.attributes?.find(
                        (attr: any) =>
                            attr.device_id === sensor.ldsuUID &&
                            Number(attr.said) === Number(sensor.said)
                    );

                    return {
                        ldsu_uuid: sensor.ldsuUID,
                        SAID: sensor.said,
                        device_name: sensor.sensorName || sensor.device_name,
                        sensor_type: sensor.sensorType || sensor.sensor_type,
                        unit: matchingAttr?.unit || sensor.unit,
                        gatewayId: sensor.gatewayId,
                        cls: sensor.cls,
                        app: sensor.app,
                        appId: sensor.appId,
                        color:
                            matchingAttr?.color ||
                            COLORS[index % COLORS.length],
                        isActuator: false,
                    };
                }) as LDSUDevice[];

            // Filter actuators from devices matching this LDSU UUID
            const filteredActuators = devices
                .filter(
                    (device: any) =>
                        device.ldsuUID === ldsuUuid && isActuator(device.cls)
                )
                .map((actuator: any, index: number) => {
                    // Find matching attribute color from panel
                    const matchingAttr = panel?.attributes?.find(
                        (attr: any) =>
                            attr.device_id === actuator.ldsuUID &&
                            Number(attr.said) === Number(actuator.said)
                    );

                    return {
                        ldsu_uuid: actuator.ldsuUID,
                        SAID: actuator.said,
                        device_name:
                            actuator.sensorName || actuator.device_name,
                        sensor_type:
                            actuator.sensorType || actuator.sensor_type,
                        unit: matchingAttr?.unit || actuator.unit,
                        gatewayId: actuator.gatewayId,
                        cls: actuator.cls,
                        app: actuator.app,
                        appId: actuator.appId,
                        color:
                            matchingAttr?.color ||
                            COLORS[
                                (filteredSensors.length + index) % COLORS.length
                            ],
                        isActuator: true,
                    };
                }) as LDSUDevice[];

            setFilterDevices({
                sensors: filteredSensors,
                actuators: filteredActuators,
            });
        } catch (error) {
            console.error("Error filtering devices:", error);
            setFilterDevices({ sensors: [], actuators: [] });
        } finally {
            setIsLoading(false);
        }
    }, [ldsuUuid, devices, panel?.attributes]);

    // Update panel attributes with new color
    const updateAttributeColor = useCallback(
        (device: LDSUDevice, color: string) => {
            if (!panel?.attributes) return;

            // Find the index of the matching attribute
            const attrIndex = panel.attributes.findIndex(
                (attr: any) =>
                    attr.device_id === device.ldsu_uuid &&
                    Number(attr.said) === Number(device.SAID)
            );

            if (attrIndex !== -1) {
                // Update the specific attribute's color
                onChange?.(`attributes[${attrIndex}].color`, color);
            }
        },
        [panel?.attributes, onChange]
    );

    // Handle color change for sensors and actuators
    const handleColorChange = useCallback(
        (device: LDSUDevice, color: string) => {
            const key = device.isActuator ? "actuators" : "sensors";
            const applyColorToMatchingDevice = (d: LDSUDevice) =>
                d.SAID === device.SAID &&
                d.ldsu_uuid === device.ldsu_uuid &&
                d.cls === device.cls
                    ? { ...d, color }
                    : d;

            setFilterDevices((prev) => ({
                ...prev,
                [key]: prev[key].map(applyColorToMatchingDevice),
            }));
            updateAttributeColor(device, color);
        },
        [updateAttributeColor]
    );

    // Widget info template rows - similar to renderUpdateContent's template?.map()
    const widgetInfoTemplate = [
        {
            key: "name",
            label: "Widget Name",
            render: () => (
                <Form.Control
                    type="text"
                    className="single-update-form-control"
                    style={{ width: "auto", minWidth: "200px" }}
                    placeholder="Enter widget name"
                    value={chartName}
                    onChange={(e) => handleNameChange(e.target.value)}
                />
            ),
        },
        {
            key: "ldsu_name",
            label: ldsuDisplayName,
            render: () => (
                <span className="mute">
                    {isLoadingLDSU ? (
                        <Spinner
                            animation="border"
                            size="sm"
                            className="mr-2"
                        />
                    ) : (
                        ldsuName
                    )}
                </span>
            ),
        },
        {
            key: "gateway_name",
            label: GATEWAY_LABEL,
            render: () => (
                <span className="mute">
                    {isLoadingLDSU ? (
                        <Spinner
                            animation="border"
                            size="sm"
                            className="mr-2"
                        />
                    ) : (
                        gatewayName
                    )}
                </span>
            ),
        },
        {
            key: "ldsu_uuid",
            label: LDSU_UUID_LABEL,
            value: ldsuUuid || "-",
        },
    ];

    return (
        <Wrapper>
            <Row className="cstm-table mb-5">
                <Col sm={12}>
                    <div className="table-line border-0 py-0">
                        {widgetInfoTemplate.map((item) => (
                            <Row
                                key={item.key}
                                className="update-row justify-content-between align-items-center"
                            >
                                <Col>{item.label}</Col>
                                <Col className="d-flex justify-content-end">
                                    {item.render ? (
                                        item.render()
                                    ) : (
                                        <span className="mute">
                                            {item.value}
                                        </span>
                                    )}
                                </Col>
                            </Row>
                        ))}
                    </div>
                </Col>
            </Row>

            {isLoading && (
                <div className="d-flex justify-content-center py-3">
                    <Spinner animation="border" size="sm" />
                    <span className="ml-2">Loading devices...</span>
                </div>
            )}

            {/* Sensor List Table */}
            {!isLoading && filterDevices.sensors.length > 0 && (
                <div className="mb-3">
                    <div className="mb-2 font-weight-bold">
                        {`Sensors on this ${ldsuDisplayName}`}
                    </div>
                    <div className="cstm-table w-100 px-2">
                        <Row className="d-none d-md-flex table-head px-2">
                            <Col md={4}>Sensor</Col>
                            <Col md={4}>Type</Col>
                            <Col md={2}>Unit</Col>
                            <Col>Color</Col>
                        </Row>

                        {filterDevices.sensors.map(
                            (sensor: LDSUDevice, index: number) => (
                                <Row
                                    key={`${sensor.ldsu_uuid}-${sensor.SAID}-${index}`}
                                    className="bg-secondary px-2 py-2 mb-2 align-items-center rounded w-100"
                                >
                                    <Col md={4}>
                                        {sensor.device_name || "-"}
                                    </Col>
                                    <Col md={4}>
                                        {sensor.sensor_type || "-"}
                                    </Col>
                                    <Col md={2}>{sensor.unit || "-"}</Col>
                                    <Col>
                                        <ColorPicker
                                            defaultValue={sensor.color}
                                            onChange={(
                                                _event: any,
                                                color: string
                                            ) =>
                                                handleColorChange(sensor, color)
                                            }
                                        />
                                    </Col>
                                </Row>
                            )
                        )}
                    </div>
                </div>
            )}

            {/* Actuator List Table */}
            {!isLoading && filterDevices.actuators.length > 0 && (
                <div className="mb-3">
                    <div className="mb-2 font-weight-bold">
                        {`Actuators on this ${ldsuDisplayName}`}
                    </div>
                    <div className="cstm-table w-100 px-2">
                        <Row className="d-none d-md-flex table-head px-2">
                            <Col md={4}>Actuator</Col>
                            <Col md={4}>Type</Col>
                            <Col md={4}>Color</Col>
                        </Row>

                        {filterDevices.actuators.map(
                            (actuator: LDSUDevice, index: number) => (
                                <Row
                                    key={`${actuator.ldsu_uuid}-${actuator.SAID}-${index}`}
                                    className="bg-secondary px-2 py-3 mb-2 align-items-center rounded"
                                >
                                    <Col md={4}>
                                        {actuator.device_name || "-"}
                                    </Col>
                                    <Col md={4}>
                                        {actuator.sensor_type || "-"}
                                    </Col>
                                    <Col md={4}>
                                        <ColorPicker
                                            defaultValue={actuator.color}
                                            onChange={(
                                                _event: any,
                                                color: string
                                            ) =>
                                                handleColorChange(
                                                    actuator,
                                                    color
                                                )
                                            }
                                        />
                                    </Col>
                                </Row>
                            )
                        )}
                    </div>
                </div>
            )}

            {/* Empty state when no sensors or actuators */}
            {!isLoading &&
                filterDevices.sensors.length === 0 &&
                filterDevices.actuators.length === 0 &&
                ldsuUuid && (
                    <div className="text-center py-3 text-muted">
                        {`No sensors or actuators found for this ${ldsuDisplayName}.`}
                    </div>
                )}
        </Wrapper>
    );
};

export { UpdateLDSUCard };
